import {
  useEffect,
  useId,
  useRef,
  useState,
  type ButtonHTMLAttributes,
  type DragEvent,
  type FormEvent,
  type HTMLAttributes,
  type ReactNode,
} from 'react'
import { createPortal } from 'react-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useLang, type Key } from '../i18n'
import {
  api,
  type CorporateTemplate,
  type OfficeTemplate,
  type OfficeTemplateKind,
  type TemplateTrim,
} from '../lib/api'
import { fileToBase64 } from '../lib/image-upload'
import {
  MAX_LIBRARY_TEMPLATES,
  ORIGIN_KEY,
  countedTemplates,
  downloadOfficeTemplate,
  hasUnpublished,
  inUseBy,
  isSixteenByNine,
  isTemplateChanged,
  officeRefusalText,
  officeTemplatesKey,
  officeTemplatesQuery,
  officeTemplateVersionsKey,
  refreshOfficeTemplates,
  templateFileRefusal,
} from '../lib/office-templates'
import { formatMegabytes } from '../lib/template-size'
import { navigate, templateViewPath } from '../lib/router'
import { IconDownload, IconHistory, IconPen, IconStar, IconTemplateLayout, IconTrash, IconUpload } from './NavIcons'
import { PromptModal } from './PromptModal'
import { TemplateDropZone } from './TemplateDropZone'

type User = { artefactId: string; title: string; slides?: number }

const TITLE_KEY = { slides: 'otpl_bid_slides_title', word: 'otpl_bid_word_title' } as const satisfies Record<OfficeTemplateKind, Key>
const ADD_KEY = { slides: 'otpl_add_slides', word: 'otpl_add_word' } as const satisfies Record<OfficeTemplateKind, Key>

/** What one write came to: its value, or why it failed (and whether a reload is the way out). */
type Outcome<T> = { ok: true; value: T } | { ok: false; text: string; changed: boolean; usedBy: User[] | null }

/** A refusal a dialog shows in place, and whether a reload is the way out. */
type Refusal = { text: string; changed: boolean }

type Dialog =
  | { kind: 'add' }
  | { kind: 'rename'; template: OfficeTemplate }
  | { kind: 'replace'; template: OfficeTemplate }
  | { kind: 'versions'; template: OfficeTemplate }
  | { kind: 'delete'; template: OfficeTemplate }

/**
 * One kind of a bid's Office templates — "Bid PowerPoint template" or "Bid Word template" — on the
 * Bid settings page (BrandingCard), over `/v1/bids/:bid/office-templates` (AB#441). Not the artefact
 * template library.
 *
 * Each row: the template's name ("Bid default (from <name>)" on the default one, for display only:
 * the stored name stays, and is what its dialogs and accessible names say), an Unpublished changes
 * badge, its origin (corporate / uploaded / imported / existing), its version, and how many artefacts
 * use it — for a PowerPoint template, and how many slides are drawn on it (AB#445: a deck's slides
 * may each name their own template; the tooltip counts them per artefact). The count says the limit
 * ("3 of 10"; an Imported template is listed and counted like any other).
 *
 * A row's actions are one line of icons, each with its word as a native tooltip (AB#464). For whoever
 * can edit the bid: Edit, Rename, Set as default, Replace (a dialog: a new version from a file, sent
 * on confirmation with the version the row showed when it opened), Download, History (the kept
 * versions, each with Restore) and Delete; "Add" takes a corporate catalogue entry (only the 16:9 ones, on a
 * chosen master) or a file. The default template's Delete is greyed, with the reason in its tooltip;
 * deleting a template artefacts use asks for the template to move them to — Delete stays disabled,
 * with the reason, until one is chosen. A viewer sees the list and Open.
 *
 * Add and Replace take a file dragged anywhere on their dialog, or picked (TemplateDropZone). A file
 * of the wrong type or over its limit is refused before it is sent; every refusal is worded
 * (lib/office-templates.ts#officeRefusalText), and a stale version offers a Reload. After any write
 * the bid's lists, template-set view, artefacts and slides template are refetched, so an open
 * slides editor redraws. A polite live region says what each write did.
 */
export function OfficeTemplateList({ bid, kind, canEdit }: { bid: string; kind: OfficeTemplateKind; canEdit: boolean }) {
  const { t, lang } = useLang()
  const qc = useQueryClient()
  const ids = useId()
  const list = useQuery(officeTemplatesQuery(bid, kind))
  // The catalogue only matters to whoever may add from it, and only the PowerPoint one exists.
  const catalogue = useQuery({
    queryKey: ['corporate-templates', 'slides'],
    queryFn: () => api.corporateTemplates('slides'),
    enabled: kind === 'slides' && canEdit,
  })
  const offered = (catalogue.data?.templates ?? []).filter((e) => isSixteenByNine(e.theme.slideSize))

  const [busy, setBusy] = useState<string | null>(null)
  const [dialog, setDialog] = useState<Dialog | null>(null)
  // The polite live-region message after each write.
  const [status, setStatus] = useState('')
  // Why the last row action was refused; `reload` = a stale version, fixed by reloading.
  const [error, setError] = useState<{ text: string; reload: boolean } | null>(null)
  // What the server trimmed off the PowerPoint file just sent.
  const [trimmed, setTrimmed] = useState<TemplateTrim | null>(null)

  useEffect(() => {
    setDialog(null)
    setStatus('')
    setError(null)
    setTrimmed(null)
  }, [bid])

  const named = (key: Key, name: string): string => t(key).replace('{name}', () => name)
  const trimText = (trim: TemplateTrim): string =>
    t('tpl_trimmed')
      .replace('{uploaded}', () => formatMegabytes(trim.uploadedBytes, lang))
      .replace('{kept}', () => formatMegabytes(trim.keptBytes, lang))
      .replace('{n}', () => String(trim.removedSlides))

  /** Run one write; on success refetch and announce `done`, on failure say nothing yet (the caller does). */
  async function run<T>(key: string, op: () => Promise<T>, done: (value: T) => string, pending = ''): Promise<Outcome<T>> {
    setBusy(key)
    setError(null)
    setTrimmed(null)
    setStatus(pending)
    try {
      const value = await op()
      await refreshOfficeTemplates(qc, bid)
      setStatus(done(value))
      return { ok: true, value }
    } catch (e) {
      setStatus('')
      const changed = isTemplateChanged(e)
      // A stale list is the likeliest cause of any other refusal: show the real one. A stale
      // version is left on screen until the member reloads, so the message matches what they see.
      if (!changed) void qc.invalidateQueries({ queryKey: officeTemplatesKey(bid) })
      return { ok: false, text: officeRefusalText(e, kind, t, lang), changed, usedBy: inUseBy(e) }
    } finally {
      setBusy(null)
    }
  }

  const fail = (o: Outcome<unknown>): void => {
    if (!o.ok) setError({ text: o.text, reload: o.changed })
  }

  async function reload(): Promise<void> {
    setError(null)
    await qc.invalidateQueries({ queryKey: officeTemplatesKey(bid) })
  }

  /** The Replace dialog's submit: null when replaced, else why not (the dialog shows it). */
  async function replace(template: OfficeTemplate, file: File): Promise<Refusal | null> {
    const res = await run(
      template.id,
      async () => api.replaceOfficeTemplate(bid, template.id, await fileToBase64(file), template.version),
      (r) => {
        const text = named('otpl_replaced', r.template.name).replace('{n}', () => String(r.template.version))
        return r.trimmed ? `${text}. ${trimText(r.trimmed)}` : text
      },
      t(kind === 'word' ? 'tpl_uploading_word' : 'tpl_uploading_slides'),
    )
    if (!res.ok) return { text: res.text, changed: res.changed }
    setTrimmed(res.value.trimmed ?? null)
    setDialog(null)
    return null
  }

  /** Reload out of a stale Replace: its next try is sent with the version now listed, or it is refused again. */
  async function reloadReplace(id: string): Promise<void> {
    await qc.invalidateQueries({ queryKey: officeTemplatesKey(bid) })
    const fresh = qc.getQueryData<OfficeTemplate[]>(officeTemplatesKey(bid, kind))?.find((x) => x.id === id)
    if (fresh) setDialog((d) => (d?.kind === 'replace' && d.template.id === id ? { kind: 'replace', template: fresh } : d))
  }

  /** The Add dialog's submit: null when added, else why not (the dialog shows it). */
  async function add(from: { catalogueId: string; master?: number } | { file: File; name: string }): Promise<string | null> {
    const res = await run(
      'add',
      // Read inside the write: a large file takes a while, with the dialog locked and its spinner on.
      async () => api.addOfficeTemplate(bid, kind, 'file' in from ? { base64: await fileToBase64(from.file), name: from.name } : from),
      (r) => {
        const text = named('otpl_added', r.template.name)
        return r.trimmed ? `${text}. ${trimText(r.trimmed)}` : text
      },
      'file' in from ? t(kind === 'word' ? 'tpl_uploading_word' : 'tpl_uploading_slides') : '',
    )
    if (!res.ok) return res.text
    setTrimmed(res.value.trimmed ?? null)
    setDialog(null)
    return null
  }

  const templates = list.data ?? []
  const counted = countedTemplates(templates)
  const full = counted >= MAX_LIBRARY_TEMPLATES
  const anyBusy = busy !== null
  const titleId = `${ids}-title`
  const fullId = `${ids}-full`

  const usage = (users: readonly User[]): string =>
    users.length === 0
      ? t('otpl_unused')
      : users.length === 1
        ? t('otpl_used_one')
        : t('otpl_used_many').replace('{n}', () => String(users.length))
  /** How many slides are drawn on a PowerPoint template (AB#445: a deck's slides may each name theirs). */
  const slidesText = (n: number): string => (n === 1 ? t('otpl_slides_one') : t('otpl_slides_many').replace('{n}', () => String(n)))
  /** The artefacts, each with its slides on the template when the server counts them. */
  const usersTitle = (users: readonly User[]): string | undefined =>
    users.map((u) => (typeof u.slides === 'number' ? `${u.title} (${slidesText(u.slides)})` : u.title)).join(', ') || undefined

  return (
    <div className="brand-comp otpl" role="group" aria-labelledby={titleId} aria-busy={anyBusy || undefined}>
      <div className="brand-comp-head otpl-head">
        <span id={titleId} className="brand-comp-title">
          {t(TITLE_KEY[kind])}
        </span>
        {list.data && (
          <span className="brand-source otpl-count">
            {t('otpl_count')
              .replace('{n}', () => String(counted))
              .replace('{max}', () => String(MAX_LIBRARY_TEMPLATES))}
          </span>
        )}
        {canEdit && list.data && (
          <button
            type="button"
            className="mini ok otpl-add"
            aria-label={t(ADD_KEY[kind])}
            aria-describedby={full ? fullId : undefined}
            disabled={anyBusy || full}
            onClick={() => setDialog({ kind: 'add' })}
          >
            {t('otpl_add')}
          </button>
        )}
      </div>
      {canEdit && full && (
        <p id={fullId} className="brand-hint">
          {t('otpl_full').replace('{max}', () => String(MAX_LIBRARY_TEMPLATES))}
        </p>
      )}
      <p className="visually-hidden" role="status" aria-live="polite">
        {status}
      </p>
      {error && (
        <div className="brand-hint brand-error otpl-error" role="alert">
          {error.text}
          {error.reload && (
            <button type="button" className="mini ghost" onClick={() => void reload()}>
              {t('otpl_reload')}
            </button>
          )}
        </div>
      )}
      {trimmed && <p className="brand-hint">{trimText(trimmed)}</p>}

      {list.isError ? (
        <p className="brand-hint brand-error" role="alert">
          {t('otpl_load_failed')}
        </p>
      ) : !list.data ? (
        <p aria-live="polite">…</p>
      ) : templates.length === 0 ? (
        <p className="brand-hint">{t('otpl_empty')}</p>
      ) : (
        <ul className="otpl-list" aria-labelledby={titleId}>
          {templates.map((tpl) => (
            <li key={tpl.id} className="otpl-row" aria-busy={busy === tpl.id || undefined}>
              <div className="otpl-main">
                <span className="otpl-name">{tpl.default ? named('otpl_default_name', tpl.name) : tpl.name}</span>
                {hasUnpublished(tpl) && <span className="badge tplv-draft-badge">{t('otpl_draft_badge')}</span>}
                {busy === tpl.id && <span className="brand-spinner" aria-hidden="true" />}
              </div>
              <p className="otpl-meta">
                <span>{t(ORIGIN_KEY[tpl.origin.type])}</span>
                <span aria-hidden="true"> · </span>
                <span>{t('otpl_version').replace('{n}', () => String(tpl.version))}</span>
                <span aria-hidden="true"> · </span>
                <span title={usersTitle(tpl.usedBy)}>{usage(tpl.usedBy)}</span>
                {typeof tpl.slides === 'number' && tpl.usedBy.length > 0 && (
                  <>
                    <span aria-hidden="true"> · </span>
                    <span className="otpl-slides">{slidesText(tpl.slides)}</span>
                  </>
                )}
              </p>
              <div className="otpl-acts">
                {/* The template view (AB#454): editors edit its shared draft there, viewers read it. */}
                <RowAction
                  label={named(canEdit ? 'otpl_edit_of' : 'otpl_open_of', tpl.name)}
                  tooltip={t(canEdit ? 'otpl_edit' : 'otpl_open')}
                  onClick={() => navigate(templateViewPath(bid, tpl.id))}
                >
                  <IconTemplateLayout />
                </RowAction>
                {canEdit && (
                  <>
                    <RowAction
                      label={named('corp_tpl_rename_of', tpl.name)}
                      tooltip={t('rename')}
                      disabled={anyBusy}
                      onClick={() => setDialog({ kind: 'rename', template: tpl })}
                    >
                      <IconPen />
                    </RowAction>
                    {!tpl.default && (
                      <RowAction
                        label={named('otpl_set_default_of', tpl.name)}
                        tooltip={t('otpl_set_default')}
                        disabled={anyBusy}
                        onClick={() =>
                          void run(tpl.id, () => api.setOfficeTemplateDefault(bid, tpl.id), () => named('corp_tpl_default_set', tpl.name)).then(fail)
                        }
                      >
                        <IconStar />
                      </RowAction>
                    )}
                    <RowAction
                      label={named('otpl_replace_of', tpl.name)}
                      tooltip={t('otpl_replace')}
                      aria-haspopup="dialog"
                      disabled={anyBusy}
                      onClick={() => setDialog({ kind: 'replace', template: tpl })}
                    >
                      <IconUpload />
                    </RowAction>
                    <RowAction
                      label={named('otpl_download_of', tpl.name)}
                      tooltip={t('otpl_download')}
                      onClick={() => void downloadOfficeTemplate(bid, tpl)}
                    >
                      <IconDownload />
                    </RowAction>
                    <RowAction
                      label={named('otpl_versions_of', tpl.name)}
                      tooltip={t('history_btn')}
                      aria-haspopup="dialog"
                      disabled={anyBusy}
                      onClick={() => setDialog({ kind: 'versions', template: tpl })}
                    >
                      <IconHistory />
                    </RowAction>
                    {/* The default's does nothing, yet is not `disabled`: its reason stays reachable by keyboard and screen reader. */}
                    <RowAction
                      label={named('otpl_delete_of', tpl.name)}
                      tooltip={t(tpl.default ? 'otpl_why_default' : 'delete')}
                      aria-disabled={tpl.default || undefined}
                      disabled={anyBusy}
                      onClick={tpl.default ? undefined : () => setDialog({ kind: 'delete', template: tpl })}
                    >
                      <IconTrash />
                    </RowAction>
                  </>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}

      {dialog?.kind === 'add' && (
        <AddDialog
          kind={kind}
          catalogue={offered}
          busy={busy === 'add'}
          onSubmit={add}
          onClose={() => setDialog(null)}
        />
      )}
      {dialog?.kind === 'rename' && (
        <PromptModal
          title={t('corp_tpl_rename_title')}
          label={t('corp_tpl_name')}
          initialValue={dialog.template.name}
          confirmLabel={t('rename')}
          onSubmit={async (name) => {
            const res = await run(dialog.template.id, () => api.renameOfficeTemplate(bid, dialog.template.id, name), () => t('corp_tpl_renamed'))
            // PromptModal shows a rejection's `serverMessage` in place, for a retry.
            if (!res.ok) throw Object.assign(new Error(res.text), { serverMessage: res.text })
            setDialog(null)
          }}
          onClose={() => setDialog(null)}
        />
      )}
      {dialog?.kind === 'replace' && (
        <ReplaceDialog
          kind={kind}
          name={dialog.template.name}
          busy={busy !== null}
          onSubmit={(file) => replace(dialog.template, file)}
          onReload={() => reloadReplace(dialog.template.id)}
          onClose={() => setDialog(null)}
        />
      )}
      {dialog?.kind === 'versions' && (
        <VersionsDialog
          bid={bid}
          template={templates.find((x) => x.id === dialog.template.id) ?? dialog.template}
          busy={busy !== null}
          onRevert={async (version, current) => {
            const name = dialog.template.name
            const res = await run(
              dialog.template.id,
              () => api.revertOfficeTemplate(bid, dialog.template.id, version, current),
              (r) =>
                named('otpl_reverted', name)
                  .replace('{v}', () => String(version))
                  .replace('{n}', () => String(r.template.version)),
            )
            return res.ok ? null : { text: res.text, changed: res.changed }
          }}
          onClose={() => setDialog(null)}
        />
      )}
      {dialog?.kind === 'delete' && (
        <DeleteDialog
          template={dialog.template}
          others={templates.filter((x) => x.id !== dialog.template.id)}
          busy={busy !== null}
          onSubmit={async (replaceWith) => {
            const { template } = dialog
            const target = templates.find((x) => x.id === replaceWith)?.name ?? ''
            const res = await run(
              template.id,
              () => api.deleteOfficeTemplate(bid, template.id, replaceWith),
              (r) =>
                r.moved.length > 0
                  ? named('otpl_deleted_moved', template.name).replace('{target}', () => target)
                  : named('otpl_deleted', template.name),
            )
            if (res.ok) {
              setDialog(null)
              return null
            }
            return { text: res.text, usedBy: res.usedBy }
          }}
          onClose={() => setDialog(null)}
        />
      )}
    </div>
  )
}

/** A row action: an icon, the action's word as its native tooltip, and an accessible name with the template's. */
function RowAction({ label, tooltip, ...button }: { label: string; tooltip: string } & ButtonHTMLAttributes<HTMLButtonElement>) {
  return <button type="button" className="otpl-ico" aria-label={label} title={tooltip} {...button} />
}

/**
 * The first control of a dialog's content, in document order. A hidden or file input cannot show
 * focus: the picker's button is the control there, wherever the file input sits.
 */
const FIRST_CONTROL = 'input:not([disabled]):not([hidden]):not([type="file"]), select:not([disabled]), button:not([disabled])'

/**
 * The dialog frame these lists and the artefact template picker share: a labelled modal dialog
 * that takes focus (its first control) and gives it back on close; Escape and the backdrop close
 * it, except while it is busy. With `fileDrop` the whole dialog takes a dragged file, unless busy;
 * the backdrop only refuses it, as the browser would otherwise leave the app to open the file.
 */
export function TemplateDialog({
  title,
  busy = false,
  fileDrop,
  onClose,
  children,
}: {
  title: string
  busy?: boolean
  fileDrop?: { onOver: (over: boolean) => void; onFiles: (files: FileList) => void }
  onClose: () => void
  children: ReactNode
}) {
  const { t } = useLang()
  const ids = useId()
  const boxRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const prev = document.activeElement as HTMLElement | null
    boxRef.current?.querySelector('.modal-body')?.querySelector<HTMLElement>(FIRST_CONTROL)?.focus()
    return () => prev?.focus()
  }, [])

  const close = (): void => {
    if (!busy) onClose()
  }

  const dragsFiles = (e: DragEvent): boolean => e.dataTransfer.types.includes('Files')
  const takesDrop = (e: DragEvent): boolean => !busy && !!boxRef.current?.contains(e.target as Node)
  const dropHandlers: HTMLAttributes<HTMLDivElement> = fileDrop
    ? {
        onDragOver: (e) => {
          if (!dragsFiles(e)) return
          e.preventDefault()
          e.dataTransfer.dropEffect = takesDrop(e) ? 'copy' : 'none'
          fileDrop.onOver(takesDrop(e))
        },
        // dragleave bubbles from the children too: only leaving the dialog itself ends the highlight.
        onDragLeave: (e) => {
          if (!boxRef.current?.contains(e.relatedTarget as Node | null)) fileDrop.onOver(false)
        },
        onDrop: (e) => {
          if (!dragsFiles(e)) return
          e.preventDefault()
          fileDrop.onOver(false)
          if (takesDrop(e)) fileDrop.onFiles(e.dataTransfer.files)
        },
      }
    : {}

  return createPortal(
    <div className="modal-backdrop" onMouseDown={close} {...dropHandlers}>
      <div
        ref={boxRef}
        className="modal prompt-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby={`${ids}-title`}
        aria-busy={busy || undefined}
        onMouseDown={(e) => e.stopPropagation()}
        onKeyDown={(e) => {
          if (e.key === 'Escape') close()
        }}
      >
        <div className="modal-head">
          <h2 id={`${ids}-title`}>{title}</h2>
          <button type="button" className="modal-close" aria-label={t('close')} onClick={close} disabled={busy}>
            ✕
          </button>
        </div>
        <div className="modal-body prompt-body">{children}</div>
      </div>
    </div>,
    document.body,
  )
}

/**
 * "Add a PowerPoint template" / "Add a Word template": from the corporate catalogue (an entry and
 * its master — PowerPoint only, the 16:9 entries only) or from a file (the drop zone + a name, which
 * follows the file's until the member types one). A file dropped on the catalogue view switches to
 * "a file". A wrong file is refused on the spot, never sent; a refusal comes back from `onSubmit` and
 * is shown in place, the dialog staying open for a retry.
 */
function AddDialog({
  kind,
  catalogue,
  busy,
  onSubmit,
  onClose,
}: {
  kind: OfficeTemplateKind
  catalogue: CorporateTemplate[]
  busy: boolean
  onSubmit: (from: { catalogueId: string; master?: number } | { file: File; name: string }) => Promise<string | null>
  onClose: () => void
}) {
  const { t, lang } = useLang()
  const ids = useId()
  const canCatalogue = kind === 'slides' && catalogue.length > 0
  const [source, setSource] = useState<'catalogue' | 'file'>('catalogue')
  const fromCatalogue = canCatalogue && source === 'catalogue'
  const [entryId, setEntryId] = useState('')
  const entry = catalogue.find((e) => e.id === entryId) ?? catalogue[0]
  const [master, setMaster] = useState<number | null>(null)
  const [name, setName] = useState('')
  const [nameTyped, setNameTyped] = useState(false)
  const [file, setFile] = useState<File | null>(null)
  const [over, setOver] = useState(false)
  const [error, setError] = useState('')
  const formRef = useRef<HTMLFormElement>(null)

  const ready = fromCatalogue ? !!entry : !!name.trim() && !!file

  // A drop, or the catalogue arriving late, swaps the view and may take what held focus with it:
  // focus stays in the dialog, where Escape works.
  useEffect(() => {
    if (document.activeElement === document.body) formRef.current?.querySelector<HTMLElement>(FIRST_CONTROL)?.focus()
  }, [fromCatalogue])

  const chooseSource = (next: 'catalogue' | 'file'): void => {
    setSource(next)
    setError('')
  }

  const take = (files: FileList | null): void => {
    const picked = files?.[0]
    if (!picked) return
    chooseSource('file')
    const why = templateFileRefusal(picked, kind, t, lang)
    setFile(why ? null : picked)
    if (why) setError(why)
    else if (!nameTyped || !name.trim()) setName(picked.name.replace(/\.[^.]+$/, ''))
  }

  const submit = async (e: FormEvent): Promise<void> => {
    e.preventDefault()
    if (!ready || busy) return
    setError('')
    let why: string | null
    if (fromCatalogue && entry) {
      const masters = entry.theme.masters.length
      why = await onSubmit({ catalogueId: entry.id, ...(masters > 1 && { master: master ?? entry.theme.master }) })
    } else if (file) {
      why = await onSubmit({ file, name: name.trim() })
    } else return
    if (why) setError(why)
  }

  return (
    <TemplateDialog
      title={t(kind === 'slides' ? 'otpl_add_slides' : 'otpl_add_word')}
      busy={busy}
      fileDrop={{ onOver: setOver, onFiles: take }}
      onClose={onClose}
    >
      <form ref={formRef} className="otpl-form" onSubmit={(e) => void submit(e)}>
        {canCatalogue && (
          <fieldset className="otpl-source">
            <legend className="prompt-label">{t('otpl_add_from')}</legend>
            <label>
              <input
                type="radio"
                name={`${ids}-source`}
                checked={source === 'catalogue'}
                disabled={busy}
                onChange={() => chooseSource('catalogue')}
              />{' '}
              {t('otpl_add_from_catalogue')}
            </label>
            <label>
              <input type="radio" name={`${ids}-source`} checked={source === 'file'} disabled={busy} onChange={() => chooseSource('file')} />{' '}
              {t('otpl_add_from_file')}
            </label>
          </fieldset>
        )}
        {fromCatalogue && entry ? (
          <>
            <label className="prompt-label">
              {t('otpl_catalogue_entry')}
              <select
                className="prompt-input"
                value={entry.id}
                disabled={busy}
                onChange={(e) => {
                  setEntryId(e.target.value)
                  setMaster(null)
                }}
              >
                {catalogue.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </label>
            {entry.theme.masters.length > 1 && (
              <label className="prompt-label">
                {t('tpl_slides_master')}
                <select
                  className="prompt-input"
                  value={master ?? entry.theme.master}
                  disabled={busy}
                  onChange={(e) => setMaster(Number(e.target.value))}
                >
                  {entry.theme.masters.map((m, i) => (
                    <option key={i} value={i}>
                      {m.name || `${t('tpl_slides_master')} ${i + 1}`}
                    </option>
                  ))}
                </select>
              </label>
            )}
          </>
        ) : (
          <>
            <TemplateDropZone kind={kind} file={file} over={over} disabled={busy} onFiles={take} />
            <label className="prompt-label">
              {t('corp_tpl_name')}
              <input
                className="prompt-input"
                value={name}
                maxLength={100}
                disabled={busy}
                onChange={(e) => {
                  setName(e.target.value)
                  setNameTyped(true)
                }}
              />
            </label>
          </>
        )}
        {error && (
          <p className="brand-hint brand-error" role="alert">
            {error}
          </p>
        )}
        <div className="prompt-acts">
          <button type="button" className="mini ghost" onClick={onClose} disabled={busy}>
            {t('cancel')}
          </button>
          <button type="submit" className="mini ok prompt-confirm" disabled={busy || !ready}>
            {busy && <span className="brand-spinner" aria-hidden="true" />}
            {t('otpl_add')}
          </button>
        </div>
      </form>
    </TemplateDialog>
  )
}

/**
 * "Replace <name>": a new version from a file, dropped or picked. A new version re-lays the decks on
 * the template, so nothing is sent until Replace is clicked. A refusal is shown in place, the dialog
 * staying open with the file chosen; a stale version asks for a Reload before another try.
 */
function ReplaceDialog({
  kind,
  name,
  busy,
  onSubmit,
  onReload,
  onClose,
}: {
  kind: OfficeTemplateKind
  name: string
  busy: boolean
  onSubmit: (file: File) => Promise<Refusal | null>
  onReload: () => Promise<void>
  onClose: () => void
}) {
  const { t, lang } = useLang()
  const [file, setFile] = useState<File | null>(null)
  const [over, setOver] = useState(false)
  const [error, setError] = useState<Refusal | null>(null)

  const take = (files: FileList | null): void => {
    const picked = files?.[0]
    if (!picked) return
    const why = templateFileRefusal(picked, kind, t, lang)
    setFile(why ? null : picked)
    setError(why ? { text: why, changed: false } : null)
  }

  const submit = async (e: FormEvent): Promise<void> => {
    e.preventDefault()
    if (!file || busy || error?.changed) return
    setError(null)
    setError(await onSubmit(file))
  }

  const reload = async (): Promise<void> => {
    await onReload()
    setError(null)
  }

  return (
    <TemplateDialog title={t('otpl_replace_of').replace('{name}', () => name)} busy={busy} fileDrop={{ onOver: setOver, onFiles: take }} onClose={onClose}>
      <form className="otpl-form" onSubmit={(e) => void submit(e)}>
        <TemplateDropZone kind={kind} file={file} over={over} disabled={busy} onFiles={take} />
        {error && (
          <div className="brand-hint brand-error otpl-error" role="alert">
            {error.text}
            {error.changed && (
              <button type="button" className="mini ghost" onClick={() => void reload()}>
                {t('otpl_reload')}
              </button>
            )}
          </div>
        )}
        <div className="prompt-acts">
          <button type="button" className="mini ghost" onClick={onClose} disabled={busy}>
            {t('cancel')}
          </button>
          <button type="submit" className="mini ok prompt-confirm" disabled={busy || !file || error?.changed}>
            {busy && <span className="brand-spinner" aria-hidden="true" />}
            {t('otpl_replace')}
          </button>
        </div>
      </form>
    </TemplateDialog>
  )
}

/**
 * A template's history: its kept versions (the last 10), newest first, the current one marked; every
 * other one offers Restore — asked in place, one row at a time, as the artefact history's RowActions
 * (AB#477), then a new version copied from it, sent with the version it replaces. A refusal is shown
 * in place; a stale version offers a Reload. The template view's History (AB#454) is this same dialog:
 * a restore while the draft holds unpublished changes is refused with who holds them. The route, the
 * API and the names here stay "revert". `canRevert` false (a viewer): the list, no Restore.
 */
export function VersionsDialog({
  bid,
  template,
  busy,
  onRevert,
  onClose,
  canRevert = true,
}: {
  bid: string
  template: OfficeTemplate
  busy: boolean
  onRevert: (version: number, current: number) => Promise<{ text: string; changed: boolean } | null>
  onClose: () => void
  canRevert?: boolean
}) {
  const { t, lang } = useLang()
  const qc = useQueryClient()
  const versions = useQuery({
    queryKey: officeTemplateVersionsKey(bid, template.id),
    queryFn: () => api.officeTemplateVersions(bid, template.id),
  })
  const [error, setError] = useState<{ text: string; changed: boolean } | null>(null)
  // The row asking "Confirm restore"; only one asks at a time.
  const [asking, setAsking] = useState<number | null>(null)
  // The row whose Restore takes the focus back: the button that had it has just left the page.
  const [refocus, setRefocus] = useState<number | null>(null)
  const confirmBtn = useRef<HTMLButtonElement>(null)
  const restoreBtn = useRef<HTMLButtonElement>(null)
  const closeBtn = useRef<HTMLButtonElement>(null)
  const when = (iso: string): string =>
    new Date(iso).toLocaleString(lang === 'fr' ? 'fr-FR' : 'en-GB', { dateStyle: 'medium', timeStyle: 'short' })

  useEffect(() => {
    if (asking !== null) confirmBtn.current?.focus()
  }, [asking])
  useEffect(() => {
    if (refocus === null || busy) return
    // Restoring the oldest kept version pushes it out of the last 10: its row is gone.
    ;(restoreBtn.current ?? closeBtn.current)?.focus()
    setRefocus(null)
  }, [refocus, busy])

  const backToRestore = (version: number): void => {
    setAsking(null)
    setRefocus(version)
  }

  async function revert(version: number, current: number): Promise<void> {
    backToRestore(version)
    setError(null)
    setError(await onRevert(version, current))
  }

  return (
    <TemplateDialog title={t('otpl_versions_of').replace('{name}', () => template.name)} busy={busy} onClose={onClose}>
      <p className="brand-hint otpl-versions-intro">{t('otpl_versions_kept')}</p>
      {error && (
        <div className="brand-hint brand-error otpl-error" role="alert">
          {error.text}
          {error.changed && (
            <button
              type="button"
              className="mini ghost"
              onClick={() => {
                setError(null)
                void qc.invalidateQueries({ queryKey: officeTemplatesKey(bid) })
              }}
            >
              {t('otpl_reload')}
            </button>
          )}
        </div>
      )}
      {versions.isError ? (
        <p className="brand-hint brand-error" role="alert">
          {t('otpl_load_failed')}
        </p>
      ) : !versions.data ? (
        <p aria-live="polite">…</p>
      ) : (
        <ol className="otpl-versions">
          {versions.data.versions.map((v) => {
            const label = t('otpl_version').replace('{n}', () => String(v.version))
            return (
              <li key={v.version} className="otpl-version">
                <span className="otpl-name">{label}</span>
                <span className="otpl-meta">{when(v.createdAt)}</span>
                {v.version === versions.data.current ? (
                  <span className="badge cat">{t('otpl_versions_current')}</span>
                ) : !canRevert ? null : asking === v.version && !busy ? (
                  <span className="hrestore-confirm">
                    <button ref={confirmBtn} type="button" className="mini ok" onClick={() => void revert(v.version, versions.data.current)}>
                      {t('hist_restore_q')}
                    </button>
                    <button type="button" className="mini ghost" onClick={() => backToRestore(v.version)}>
                      {t('cancel')}
                    </button>
                  </span>
                ) : (
                  <button
                    ref={refocus === v.version ? restoreBtn : undefined}
                    type="button"
                    className="hrestore"
                    aria-label={t('otpl_revert_to').replace('{n}', () => String(v.version))}
                    title={t('otpl_restore_title')}
                    disabled={busy}
                    onClick={() => setAsking(v.version)}
                  >
                    ↶ {t('hist_restore')}
                  </button>
                )}
              </li>
            )
          })}
        </ol>
      )}
      <div className="prompt-acts">
        <button ref={closeBtn} type="button" className="mini ghost" onClick={onClose} disabled={busy}>
          {t('close')}
        </button>
      </div>
    </TemplateDialog>
  )
}

/**
 * Delete a template. When artefacts use it (as the list said, or as the server's 409 `in_use`
 * says), it cannot go until they move: the dialog names them, asks for the template to move them
 * to, and keeps Delete disabled — with the reason — until one is chosen.
 */
function DeleteDialog({
  template,
  others,
  busy,
  onSubmit,
  onClose,
}: {
  template: OfficeTemplate
  others: OfficeTemplate[]
  busy: boolean
  onSubmit: (replaceWith: string | undefined) => Promise<{ text: string; usedBy: User[] | null } | null>
  onClose: () => void
}) {
  const { t } = useLang()
  const ids = useId()
  const [usedBy, setUsedBy] = useState<User[]>(template.usedBy)
  const [replaceWith, setReplaceWith] = useState('')
  const [error, setError] = useState('')
  const used = usedBy.length > 0
  const blocked = used && !replaceWith
  const named = (key: Key): string => t(key).replace('{name}', () => template.name)

  const submit = async (e: FormEvent): Promise<void> => {
    e.preventDefault()
    if (blocked || busy) return
    setError('')
    const res = await onSubmit(used ? replaceWith : undefined)
    if (!res) return
    setError(res.text)
    if (res.usedBy) setUsedBy(res.usedBy)
  }

  return (
    <TemplateDialog title={t('otpl_delete_title')} busy={busy} onClose={onClose}>
      <form className="otpl-form" onSubmit={(e) => void submit(e)}>
        <p className="confirm-msg">
          {used ? named('otpl_delete_used').replace('{titles}', () => usedBy.map((u) => u.title).join(', ')) : named('otpl_delete_msg')}
        </p>
        {used && (
          <label className="prompt-label">
            {t('otpl_replace_with')}
            <select className="prompt-input" value={replaceWith} disabled={busy} onChange={(e) => setReplaceWith(e.target.value)}>
              <option value="">{t('otpl_replace_with_pick')}</option>
              {others.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.name}
                </option>
              ))}
            </select>
          </label>
        )}
        {blocked && (
          <p id={`${ids}-why`} className="brand-hint">
            {t('otpl_delete_why_used')}
          </p>
        )}
        {error && (
          <p className="brand-hint brand-error" role="alert">
            {error}
          </p>
        )}
        <div className="prompt-acts">
          {busy && <span className="brand-spinner" aria-hidden="true" />}
          <button type="button" className="mini ghost" onClick={onClose} disabled={busy}>
            {t('cancel')}
          </button>
          <button type="submit" className="mini danger" disabled={busy || blocked} aria-describedby={blocked ? `${ids}-why` : undefined}>
            {t('delete')}
          </button>
        </div>
      </form>
    </TemplateDialog>
  )
}
